import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '../../context/ThemeContext';

export interface PageHeaderProps {
  /** Page title */
  title: string;
  /** Optional subtitle / description */
  subtitle?: string;
  /** Right-side actions (buttons, filters, etc.) */
  actions?: React.ReactNode;
  /** data-testid forwarded to root element */
  'data-testid'?: string;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  actions,
  'data-testid': testId = 'page-header',
}) => {
  const { theme } = useTheme();

  return (
    <Box
      data-testid={testId}
      sx={{
        mb: { xs: 2, sm: 3 },
        display: 'flex',
        // Stack title above actions on mobile so action buttons don't crush the title.
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'flex-start' },
        gap: { xs: 1.25, sm: 2 },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontWeight: 700,
            color: theme.text,
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: theme.gray[600],
              mt: 0.5,
              fontSize: { xs: '0.813rem', sm: '0.875rem' },
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {actions && (
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1, sm: 1.5 },
            flexShrink: 0,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default PageHeader;
